import { Button, Card, Modal, ModalCloseButton } from '@presentation/components';
import { BREAKPOINTS } from '@presentation/styles';
import { useTranslation } from '@presentation/utils';
import { convertHeicToBlob } from '@application/utils';
import imageCompression from 'browser-image-compression';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Cropper as CropperComponent } from 'react-cropper';
import { useMediaQuery } from 'react-responsive';
import * as Styled from './styled';
import type { ImageCropperModalProps } from './types';

import 'cropperjs/dist/cropper.css';

export const IMAGE_CROPPER_DEFAULT_MAX_COMPRESSED_SIZE = 8000000;

export const ImageCropperModal: FC<ImageCropperModalProps> = (props) => {
  const {
    modal,
    file,
    onSave,
    maxCompressedSize = IMAGE_CROPPER_DEFAULT_MAX_COMPRESSED_SIZE,
    log,
  } = props;
  const { translate } = useTranslation();

  const [error, setError] = useState<string | undefined>(undefined); // EXCEPTIONAL USE OF STATE IN PRESENTATION, THIS COMPONENT IS USED TOO MUCH AND TOO DEEPLY NESTED.
  const [progress, setProgress] = useState<number | undefined>(undefined); // EXCEPTIONAL USE OF STATE IN PRESENTATION, THIS COMPONENT IS USED TOO MUCH AND TOO DEEPLY NESTED.
  const cropperRef = useRef<HTMLImageElement>(null);

  // Hack to force the cropper to update, because of layout problems
  useEffect(() => {
    setError(undefined);
    setProgress(undefined);
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      const imageElement: any = cropperRef?.current;
      const cropper: Cropper = imageElement?.cropper;
      cropper?.reset();
    }, 1);
  }, [modal.open]);

  const [src, setSrc] = useState<string | undefined>(undefined);

  const convertFileFromHeic = useCallback(async (fileToConvert: File) => {
    const blob = await convertHeicToBlob(fileToConvert);
    setSrc(URL.createObjectURL(blob));
  }, []);

  useEffect(() => {
    setError(undefined);
    setProgress(undefined);
    if (!file) setSrc('');
    else if (typeof window === 'undefined') setSrc('');
    else if (!(file instanceof File)) setSrc('');
    else if (file.type === 'image/heic' || file.type === 'image/heif') {
      log?.({
        content: `Converting original ${file.type} to png ${file.name} ${file.size} ${file.type}`,
        params: { name: file.name, size: file.size, type: file.type },
      });
      convertFileFromHeic(file);
    } else setSrc(URL.createObjectURL(file));
  }, [file]);

  const filename = useMemo(() => {
    if (file?.name) {
      return ['.heic', '.heif'].some((type) => file.name.toLowerCase().endsWith(type))
        ? file.name.toLowerCase().replaceAll('.heic', '.png').replaceAll('.heif', '.png')
        : file?.name;
    }
    return '';
  }, [file]);

  const saveClickHandler = useCallback(async () => {
    const imageElement: any = cropperRef?.current;
    const cropper: any = imageElement?.cropper;

    cropper?.getCroppedCanvas().toBlob(async (blob: Blob) => {
      setError(undefined);
      setProgress(0);
      try {
        if (!file) throw new Error('file/not-found');
        const croppedFile = new File([blob], filename, {
          lastModified: file.lastModified,
          type: file.type,
        });

        if (!croppedFile) throw new Error('blob/not-found');

        const compressedBlob = await imageCompression(croppedFile, {
          maxSizeMB: maxCompressedSize / 1000000,
          maxWidthOrHeight: 1920,
          onProgress: (percent: number) => {
            setProgress(percent);
          },
          useWebWorker: true,
        });

        if (!compressedBlob) throw new Error('file/not-found');
        if (maxCompressedSize && compressedBlob.size > maxCompressedSize) {
          throw new Error('file/too-large');
        }
        const compressedFile = new File([compressedBlob], filename, {
          lastModified: compressedBlob.lastModified,
          type: compressedBlob.type,
        });

        onSave?.(compressedFile);
        if (src) URL.revokeObjectURL(src);
      } catch (err) {
        if ((err as any)?.message === 'file/too-large') {
          setError(translate('common.validation.fileTooBig'));
        } else {
          log?.(`ImageCropper Error ${err?.message ?? err}`);
          setError('Error');
        }
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        setProgress(undefined);
      }
    });
  }, [file, filename, log, maxCompressedSize, onSave, src, translate]);

  const smallBreakpoint = useMediaQuery({ query: `(max-width: ${BREAKPOINTS.small}px)` });

  return (
    <Modal {...modal}>
      <Card size="medium" isModal>
        <ModalCloseButton modal={modal} />
        <Styled.Wrapper>
          <CropperComponent
            src={src}
            style={{
              height: smallBreakpoint ? 400 : 532,
              width: smallBreakpoint ? 280 : 700,
              margin: 'auto',
              maxWidth: '100%',
            }}
            aspectRatio={4 / 3}
            viewMode={1}
            ref={cropperRef}
          />
          {error && <Styled.StyledErrorText>{error}</Styled.StyledErrorText>}
          <Styled.ButtonContainer>
            <Button
              onClick={saveClickHandler}
              color="blue"
              disabled={!!error}
              loading={progress != null}
            >
              {translate('common.buttons.save')}
              {progress != null && ` (${progress}%)`}
            </Button>
          </Styled.ButtonContainer>
        </Styled.Wrapper>
      </Card>
    </Modal>
  );
};
